import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getProfile } from '@/lib/api';
import { ProductId } from '@/contexts/ProductContext';
import { useAdminScope, usePermission, PermissionLevel } from '@/hooks/usePermission';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Requires user to have specific permission for a resource
   * @example requireResource={{ resource: 'content_articles', level: 'edit' }}
   */
  requireResource?: {
    resource: string;
    level?: PermissionLevel;
  };
}

/**
 * ProtectedRoute - Route wrapper that checks authentication and authorization
 *
 * Uses resource-based permission checks for access control.
 *
 * @example
 * ```tsx
 * // Require specific permission
 * <ProtectedRoute requireResource={{ resource: 'content_articles', level: 'edit' }}>
 *   <ArticleEditor />
 * </ProtectedRoute>
 * ```
 *
 * @example
 * ```tsx
 * // Just require authentication (no specific permission)
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute = ({
  children,
  requireResource,
}: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);

  // Permission system hooks
  const { scope, isLoading: scopeLoading } = useAdminScope();

  // Resource permission check (if requireResource specified)
  const { hasPermission, isLoading: permissionLoading } = usePermission(
    requireResource?.resource || '',
    requireResource?.level || 'read'
  );

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Check authentication
        const { data: { user: currentUser } } = await supabase.auth.getUser();

        if (!currentUser) {
          navigate('/auth', { state: { from: location.pathname } });
          return;
        }

        // Check email verification
        if (!currentUser.email_confirmed_at) {
          navigate('/verify-email');
          return;
        }

        // Wait for permission hooks to finish loading before checking authorization
        const stillLoading = (requireResource && permissionLoading) || scopeLoading;

        if (stillLoading) {
          // Still loading, don't make any authorization decisions yet
          return;
        }

        // Check resource-based permission
        if (requireResource) {
          if (!hasPermission) {
            console.warn(
              `User lacks required permission: ${requireResource.resource} (${requireResource.level || 'read'})`
            );

            // For admin routes, always redirect to admin panel (not user dashboard)
            const isAdminRoute = location.pathname.startsWith('/admin');
            if (isAdminRoute || scope?.is_admin) {
              navigate('/admin');
            } else {
              // Get user's primary product for redirect
              const profileResult = await getProfile(currentUser.id);
              const productId =
                profileResult.success && profileResult.data?.primary_product_id
                  ? profileResult.data.primary_product_id
                  : ProductId.MINDCOTINE;
              navigate(`/${productId}/dashboard`);
            }
            return;
          }
        }

        setIsAuthorized(true);
      } catch (error) {
        console.error('Error checking access:', error);
        navigate('/auth', { state: { from: location.pathname } });
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [
    navigate,
    location.pathname,
    requireResource,
    hasPermission,
    permissionLoading,
    scopeLoading,
    scope,
  ]);

  if (loading || permissionLoading || scopeLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return isAuthorized ? <>{children}</> : null;
};
